import { Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useEffect } from 'react';
import { Add, FileOpenOutlined, SaveOutlined } from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import {
    FormFieldDto,
    FormFieldType,
    FormItemKind,
    DynamicSectionDto,
    RepeatType,
    UploadTarget,
} from './document-generation.dto';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import {
    addFieldToTemplate,
    addSectionToTemplate,
    createDocumentTemplate,
    loadDataCatalaog,
    loadDocumentTemplateById,
    updateDocumentTemplate,
    updateTemplate,
} from './document-generation-template.slice';
import { generateSimpleID } from 'src/common/generateSimpleID';

import { SortableDocumentTemplateItem } from './components/DocumentTemplateItem';
import { IS_DEV } from 'src/appConfig';

export default function DocumentTemplateCreateEditPage() {
    const { t } = useTranslation();

    const template = useAppSelector((state) => state.documentGenerationTemplate.template);
    const isLoading = useAppSelector((state) => state.documentGenerationTemplate.isLoading);

    const { id } = useParams();

    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const EMPTY_FIELD: FormFieldDto = {
        kind: FormItemKind.FIELD,
        _id: generateSimpleID(),
        key: generateSimpleID(),
        label: '',
        type: FormFieldType.TEXT,
        config: {
            required: true,
            options: [],
            defaultValue: null,
            placeholder: '',
            dynamicOptionsKey: null,
            validation: {
                minLength: null,
                maxLength: null,
                minValue: null,
                maxValue: null,
                pattern: null,
            },
        },
    };

    const EMPTY_SECTION: DynamicSectionDto = {
        kind: FormItemKind.SECTION,
        _id: generateSimpleID(),
        key: generateSimpleID(),
        label: '',
        items: [],
        repeat: {
            type: RepeatType.NON_REPEATING,
        },
        visibleWhen: null,
    };

    useEffect(() => {
        dispatch(loadDataCatalaog());
    }, [dispatch]);

    useEffect(() => {
        refreshTemplate();
    }, [id]);

    const addEmptyField = () => {
        dispatch(addFieldToTemplate(EMPTY_FIELD));
    };

    const addEmptySection = () => {
        dispatch(addSectionToTemplate(EMPTY_SECTION));
    };

    const refreshTemplate = () => {
        if (id) {
            dispatch(loadDocumentTemplateById(id));
        } else {
            dispatch(
                updateTemplate({
                    name: '',
                    templateUrl: '',
                    formSchema: [],
                    folderRegex: '',
                    fileNameTemplate: '',
                    uploadTarget: UploadTarget.GOOGLE_DRIVE,
                }),
            );
        }
    };

    const getUploadTargetName = (target: UploadTarget) => {
        switch (target) {
            case UploadTarget.LOCAL:
                return 'Local';
            case UploadTarget.GOOGLE_DRIVE:
                return 'Google Drive';
            case UploadTarget.NOVA_AVA:
                return 'NOVA AVA';
            default:
                throw new Error(`Unknown upload target: ${target}`);
        }
    };

    async function createOrUpdate(event: React.FormEvent<HTMLFormElement>) {
        console.debug('Creating template with values:');
        event.preventDefault();

        if (!template.id) {
            await dispatch(createDocumentTemplate(template));
        } else {
            await dispatch(updateDocumentTemplate(template));
        }

        navigate('/document-generation/templates');
    }

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = template.formSchema.findIndex((field) => (field._id ?? field.key) === active.id);
            const newIndex = template.formSchema.findIndex((field) => (field._id ?? field.key) === over.id);
            dispatch(
                updateTemplate({
                    ...template,
                    formSchema: arrayMove(template.formSchema, oldIndex, newIndex),
                }),
            );
        }
    }

    return (
        <form onSubmit={createOrUpdate} style={{ width: '100%' }}>
            {isLoading && (
                <Grid container direction="row" sx={{ justifyContent: 'center', alignItems: 'center' }} size={12}>
                    <Typography variant="h6">{t('Loading...')}</Typography>
                </Grid>
            )}

            <Grid size={12} spacing={5} container direction="row" sx={{ justifyContent: 'left', alignItems: 'left' }}>
                <Grid container direction="row" spacing={5}>
                    <Grid size={12}>
                        <Typography variant="h4">{t('Create New Template')}</Typography>
                    </Grid>
                </Grid>

                <Grid
                    size={12}
                    spacing={2}
                    container
                    direction="row"
                    sx={{ justifyContent: 'center', alignItems: 'center' }}
                >
                    <TextField
                        fullWidth
                        required
                        label={t('Name')}
                        size="small"
                        placeholder={t('Template Name')}
                        value={template.name}
                        onChange={(e) => dispatch(updateTemplate({ ...template, name: e.target.value }))}
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                    />

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                    >
                        <TextField
                            fullWidth
                            required
                            label={t('URL')}
                            placeholder={t('Template URL')}
                            value={template.templateUrl}
                            size="small"
                            onChange={(e) => dispatch(updateTemplate({ ...template, templateUrl: e.target.value }))}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                        />
                        <Link to={template.templateUrl} target="_blank" rel="noopener noreferrer">
                            <Button variant="outlined" startIcon={<FileOpenOutlined />}>
                                {t('Open')}
                            </Button>
                        </Link>
                    </Stack>

                    <TextField
                        fullWidth
                        required
                        label={t('Storing Folder')}
                        placeholder={t('Storing Folder')}
                        value={template.folderRegex}
                        size="small"
                        onChange={(e) => dispatch(updateTemplate({ ...template, folderRegex: e.target.value }))}
                        helperText={t('Storing Folder regex help text')}
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                    />
                    <FormControl fullWidth required size="small">
                        <InputLabel shrink>{t('Upload Target')}</InputLabel>
                        <Select
                            label={t('Upload Target')}
                            value={template.uploadTarget}
                            onChange={(e) =>
                                dispatch(updateTemplate({ ...template, uploadTarget: e.target.value as UploadTarget }))
                            }
                        >
                            {Object.values(UploadTarget).map(
                                (target) =>
                                    (IS_DEV || target !== UploadTarget.LOCAL) && (
                                        <MenuItem key={target} value={target}>
                                            {getUploadTargetName(target)}
                                        </MenuItem>
                                    ),
                            )}
                        </Select>
                    </FormControl>

                    {template.uploadTarget !== UploadTarget.NOVA_AVA && (
                        <TextField
                            fullWidth
                            required
                            label={t('File name template')}
                            placeholder={t('e.g., {customerName}_{date}')}
                            value={template.fileNameTemplate || ''}
                            size="small"
                            onChange={(e) =>
                                dispatch(updateTemplate({ ...template, fileNameTemplate: e.target.value }))
                            }
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                        />
                    )}
                </Grid>

                <Grid size={12} container spacing={3} sx={{ justifyContent: 'right' }}>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext
                            items={template.formSchema.map((item) => item._id ?? item.key)}
                            strategy={verticalListSortingStrategy}
                        >
                            {template.formSchema.map((item, index) => (
                                <SortableDocumentTemplateItem key={item._id ?? item.key} index={index} item={item} />
                            ))}
                        </SortableContext>
                    </DndContext>
                </Grid>
                <Grid size={12} container spacing={3} sx={{ justifyContent: 'right' }}>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="contained"
                            onClick={() => addEmptyField()}
                            color="secondary"
                            startIcon={<Add />}
                        >
                            {t('Add Field')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => addEmptySection()}
                            color="secondary"
                            startIcon={<Add />}
                        >
                            {t('Add Section')}
                        </Button>
                    </Stack>
                </Grid>

                <Grid size={12} spacing={0} container>
                    <Button
                        variant="contained"
                        type="submit"
                        color="primary"
                        disabled={isLoading}
                        loading={isLoading}
                        startIcon={<SaveOutlined />}
                    >
                        {t('Save Template')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
}
